import React, { createContext, useRef, useEffect, useReducer } from 'react';
import { BASEJUMP_WS_API_URL } from '../constants';
export const WebSocketContext = createContext(null);

// Action types
const WS_CONNECTED = 'WS_CONNECTED';
const WS_DISCONNECTED = 'WS_DISCONNECTED';
const WS_MESSAGE = 'WS_MESSAGE';
const WS_ERROR = 'WS_ERROR';
const WS_PING_SENT = 'WS_PING_SENT';
const WS_PONG_RECEIVED = 'WS_PONG_RECEIVED';

// Reducer to handle WebSocket state
const wsReducer = (state, action) => {
  switch (action.type) {
    case WS_CONNECTED:
      return { ...state, isConnected: true, error: null };
    case WS_DISCONNECTED:
      return { ...state, isConnected: false };
    case WS_MESSAGE:
      return { ...state, lastMessage: action.payload };
    case WS_ERROR:
      return { ...state, error: action.payload };
    case WS_PING_SENT:
      return { ...state, lastPingTime: Date.now() };
    case WS_PONG_RECEIVED:
      return { ...state, lastPongTime: Date.now() };
    default:
      return state;
  }
};

export const WebSocketProvider = ({ basejumpToken, children, pingInterval = 30000 }) => {
  const [state, dispatch] = useReducer(wsReducer, {
    isConnected: false,
    lastMessage: null,
    error: null
  });
  const wsRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const pongTimeoutRef = useRef(null);

  const clearTimers = () => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (pongTimeoutRef.current) {
      clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = null;
    }
  };
  
  // Send ping message
  const sendPing = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const pingMessage = { type: 'ping', timestamp: Date.now() };
      console.log('Ping sent');
      wsRef.current.send(JSON.stringify(pingMessage));
      dispatch({ type: WS_PING_SENT });
      
      // Set timeout for pong response (10 seconds)
      pongTimeoutRef.current = setTimeout(() => {
        console.warn('Pong timeout - closing connection');
        if (wsRef.current) {
          wsRef.current.close();
        }
      }, 10000);
    }
  };
  
  // Start ping interval
  const startPingInterval = () => {
    pingIntervalRef.current = setInterval(sendPing, pingInterval);
  };

  useEffect(() => {
    const tryToReconnect = () => {
      dispatch({ type: WS_DISCONNECTED });
      clearTimers();
      // Attempt to reconnect after 3 seconds
      console.error('Reconnecting in 3 sec...');
      wsRef.current = null;
      setTimeout(connect, 3000);
    };

    const connect = () => {
      const url = `${BASEJUMP_WS_API_URL}?token=${basejumpToken}`;
      if (wsRef.current) {
        console.warn('We are already connected!');
        return;
      }

      try {
        wsRef.current = new WebSocket(url);
      } catch (err) {
        tryToReconnect();
        return;
      }

      wsRef.current.onopen = () => {
        dispatch({ type: WS_CONNECTED });
        startPingInterval();
      };
     
      wsRef.current.onclose = () => {
        tryToReconnect();
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // Handle pong response
          if (data.type === 'pong') {
            console.log('Pong received');
            dispatch({ type: WS_PONG_RECEIVED });
            // Clear the pong timeout since we received a response
            if (pongTimeoutRef.current) {
              clearTimeout(pongTimeoutRef.current);
              pongTimeoutRef.current = null;
            }
            return;
          }
          
          // Handle ping from server (respond with pong)
          if (data.type === 'ping') {
            const pongMessage = { type: 'pong', timestamp: Date.now() };
            wsRef.current.send(JSON.stringify(pongMessage));
            return;
          }

          dispatch({ type: WS_MESSAGE, payload: data });
        } catch (error) {
          dispatch({ type: WS_ERROR, payload: 'Failed to parse message' });
        }
      };
      
      wsRef.current.onerror = (error) => {
        dispatch({ type: WS_ERROR, payload: error.message });
      };
    };
    if (basejumpToken) { 
      connect();
    }
    
    // Cleanup on unmount
    return () => {
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [basejumpToken]);
  
  const sendMessage = (message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
    }
  };
  
  return (
    <WebSocketContext.Provider value={{ ...state, sendMessage }}>
      {children}
    </WebSocketContext.Provider>
  );
};
